import { useTranslation } from "react-i18next"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { Avatar } from "@mantine/core"
import { Suspense } from "react"
import { PersonProfilePictureAvatar } from "../../-components/person-profile-picture-avatar"
import { TheasoftColumnDef } from "@/routes/-components/custom-tanstack-table"
import { VisibilityState } from "@tanstack/react-table"
import { PersonDto } from "@/orval/gen"

type PersonTableColumns = {
  baseColumns: TheasoftColumnDef<PersonDto>[]
  extendedColumns: TheasoftColumnDef<PersonDto>[]
  defaultColumnVisibility: VisibilityState
}

export function usePersonTableColumns(): PersonTableColumns {
  const { t } = useTranslation("app_server")

  const { data: communicationTypes } = useSuspenseQueryGet(
    "/api/CommunicationType/GetCommunicationTypes",
    null
  )

  const PersonAvatar = React.memo(
    ({ person, size }: { person: PersonDto; size: number }) => (
      <PersonProfilePictureAvatar personId={person.personId} size={size} />
    )
  )

  const baseColumns = React.useMemo<TheasoftColumnDef<PersonDto>[]>(
    () => [
      {
        accessorFn: (row) => row.personId,
        id: "avatar",
        header: "",
        cell: ({ row }) => (
          <Suspense fallback={<Avatar size={36} />}>
            <PersonAvatar person={row.original} size={36} />
          </Suspense>
        ),
        enableResizing: false,
        enableSorting: false,
        size: 48,
      },
      { accessorKey: "firstName", header: t("FirstName") },
      { accessorKey: "lastName", header: t("LastName") },
      {
        id: "salutation",
        header: t("Salutation"),
        cell: ({ row }) => row.original.salutation?.name,
        enableSorting: false,
      },
      {
        accessorKey: "categories",
        header: t("ContactArea"),
        enableSorting: false,
        cell: ({ cell }) => cell.getValue<string[]>()?.join(", "),
      },
      { accessorKey: "function", header: t("Function"), enableSorting: false },
    ],
    []
  )

  const extendedColumns = React.useMemo<TheasoftColumnDef<PersonDto>[]>(
    () => [
      { accessorKey: "id", header: "Id" },
      ...baseColumns,
      {
        accessorKey: "academicTitle",
        header: t("Title"),
        enableSorting: false,
      },
      { accessorKey: "personnelNo", header: t("PersonnelNumber") },
      { accessorKey: "keyName", header: t("KeyName") },
      { accessorKey: "keyWords", header: t("KeyWords"), enableSorting: false },
      {
        id: "street",
        header: t("Street"),
        cell: ({ row }) => row.original.firstAdress?.street,
        enableSorting: false,
      },
      {
        id: "postalCode",
        header: t("PostalCodeShort"),
        cell: ({ row }) => row.original.firstAdress?.postalCode,
        enableSorting: false,
      },
      {
        id: "city",
        header: t("City"),
        cell: ({ row }) => row.original.firstAdress?.city,
        enableSorting: false,
      },
      //{ id: "country",
      //header: t("Country"),
      //cell: ({ row }) => row.original.adress?.countryId, //TODO
      //enableSorting: false },
      ...communicationTypes?.map<TheasoftColumnDef<PersonDto>>(
        (communicationType) => ({
          accessorFn: (row) =>
            row.contactData?.find(
              (contactData) =>
                contactData !== null &&
                contactData.typeId === communicationType.communicationTypeId
            )?.value,
          header: communicationType.name,
          id: "communicationType-" + communicationType.communicationTypeId,
          enableSorting: false,
        })
      ),
    ],
    [communicationTypes]
  )

  const defaultColumnVisibility = React.useMemo<VisibilityState>(
    () => ({
      avatar: true,
      id: false,
      keyName: false,
      keyWords: false,
      street: false,
      academicTitle: false,
      salutation: false,
      postalCode: false,
      city: false,
      country: false,
      ...communicationTypes.reduce(
        (acc, v) => {
          acc[`communicationType-${v.communicationTypeId}`] = false
          return acc
        },
        {} as Record<string, boolean>
      ),
    }),
    [communicationTypes]
  )

  return { baseColumns, extendedColumns, defaultColumnVisibility }
}
